import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import ExportModalImage from "../../components/ExportModalImage";
import { documentService } from "../../services/documentService";
import { postPreLbaData } from "../../services/postPreLbaDataService";
import CTAs from "../../components/CTAs";
import DownloadModalTables from "../../components/DownloadModalTables";
import DateFilter from "../../components/DateFilter";
import Footer from "../../components/Footer.js";
import { getCurrentUser } from "../../utils/getCurrentUser";

const PostLBAActionsPage = () => {
  const navigate = useNavigate();
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] = useState(false);
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isExportModalImageOpen, setExportModalImageOpen] = useState(false);
  const [uploadCount, setUploadCount] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedDateTimeRange, setSelectedDateTimeRange] = useState("");
  const [totalSubmitted, setTotalSubmitted] = useState(0);
  const [settledCases, setSettledCases] = useState(0);
  const [dynamicData, setDynamicData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isDateFilterOpen, setDateFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;

  const user = getCurrentUser();

  useEffect(() => {
    async function fetchDataAndCount() {
      try {
        const total = await documentService.getTotalUploadCount();
        setUploadCount(total);
      } catch (error) {
        console.error("Failed to fetch data or upload count", error);
      }
    }
    fetchDataAndCount();
  }, []);

  useEffect(() => {
    const storedUserId = user?.id;
    if (storedUserId) {
      setUserId(storedUserId);
      const fetchData = async () => {
        const result = await postPreLbaData.getPostPreLbaData(storedUserId);
        if (result.success) {
          setUploadCount(result.totalAmount);
          setTotalSubmitted(result.totalSubmitted);
          setSettledCases(result.settledCases);
          const transformedData = result.details.map((item) => ({
            id: item.lbaId,
            debtor: item.debtorName,
            amount: `£${item.amount}`,
            date: new Date(item.createdAt).toLocaleDateString("en-US", {
              year: "numeric", month: "long", day: "numeric",
            }),
            risk: item.riskStatus,
            action: "See details",
            raw: item,
          }));
          setDynamicData(transformedData);
          setFilteredData(transformedData);
        } else {
          console.error(result.error);
        }
      };
      fetchData();
    }
  }, []);

  const onClickViewDetails = useCallback(() => navigate("/lba"), [navigate]);
  const closeNavigationDropdown = useCallback(() => setNavigationDropdownOpen(false), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);
  const openExportModalImage = useCallback(() => setExportModalImageOpen(true), []);
  const closeExportModalImage = useCallback(() => setExportModalImageOpen(false), []);
  const openDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(true), []);
  const closeDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(false), []);
  const openDateFilter = useCallback(() => setDateFilterOpen(true), []);
  const closeDateFilter = useCallback(() => setDateFilterOpen(false), []);
  const handleDateTimeSelect = (dateRange) => setSelectedDateTimeRange(dateRange);

  const onSeeDetailsTextClick = useCallback(
    (lbaId, name, amount, date, risk, address, email) => {
      navigate("/postprelbadet", {
        state: { lbaId, name, amount, date, risk, address, email, lawyer: false },
        replace: true,
      });
    },
    [navigate]
  );

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("en-GB");
  const formatTime = (dateString) => {
    const d = new Date(dateString);
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  };

  const filteredDebtors =
    activeTab === "all"
      ? dynamicData
      : dynamicData.filter((d) => d.risk.toLowerCase() === activeTab.toLowerCase());

  const searchedDebtors = filteredDebtors.filter(
    (d) =>
      d.debtor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.amount.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.date.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.risk.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.action.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const finalDebtors =
    selectedDateTimeRange?.startDate && selectedDateTimeRange?.endDate
      ? searchedDebtors.filter((debtor) => {
          const submissionDate = formatDate(debtor.raw.createdAt);
          const submissionTime = formatTime(debtor.raw.createdAt);
          const { startDate, endDate, fromTime, toTime } = selectedDateTimeRange;
          const withinDateRange = submissionDate >= startDate && submissionDate <= endDate;
          const withinTimeRange = fromTime && toTime ? submissionTime >= fromTime && submissionTime <= toTime : true;
          return withinDateRange && withinTimeRange;
        })
      : searchedDebtors;

  useEffect(() => { setCurrentPage(1); }, [selectedDateTimeRange, activeTab, searchQuery]);

  const totalPages = Math.ceil(finalDebtors.length / itemsPerPage);
  const paginatedDebtors = finalDebtors.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const riskStyleMap = {
    "High Risk":   { background: "#FEF2F2", color: "#991B1B" },
    "Medium Risk": { background: "#FFFBEB", color: "#92400E" },
    "Low Risk":    { background: "#F0FDF4", color: "#166534" },
  };

  const metricCards = [
    {
      label: "Total Post Pre-LBA sent",
      value: totalSubmitted,
      format: "count",
      accentBar: "#7C3AED",
      iconBg: "#F5F3FF",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M15.24 2H8.76C5 2 4.71 5.38 6.74 7.22l10.52 9.56C19.29 18.62 19 22 15.24 22H8.76C5 22 4.71 18.62 6.74 16.78l10.52-9.56C19.29 5.38 19 2 15.24 2Z"
            stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      label: "Total Amount",
      value: uploadCount,
      format: "currency",
      accentBar: "#7C3AED",
      iconBg: "#F5F3FF",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M9.5 13.75c0 .97.75 1.75 1.67 1.75h1.88c.8 0 1.45-.68 1.45-1.53 0-.91-.4-1.24-.99-1.45l-3.01-1.05c-.59-.21-.99-.53-.99-1.45 0-.84.65-1.53 1.45-1.53h1.88c.92 0 1.67.78 1.67 1.75M12 7.5v9"
            stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 12c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M17 3v4h4M22 2l-5 5" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      label: "Settled Cases",
      value: settledCases,
      format: "count",
      accentBar: "#059669",
      iconBg: "#ECFDF5",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z"
            stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m7.75 12 2.83 2.83 5.67-5.66" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

        .plba-root * { box-sizing: border-box; }
        .plba-root {
          font-family: 'DM Sans', sans-serif;
          background: #F1F5F9;
          min-height: 100vh;
        }

        .plba-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease, transform 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .plba-metric-card { cursor: default; }
        .plba-metric-card:hover { transform: translateY(-3px); box-shadow: 0 4px 24px rgba(0,0,0,0.09); }
        .plba-metric-card .accent-bar {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          border-radius: 16px 16px 0 0;
        }

        .plba-tab-row {
          display: inline-flex;
          background: #F1F5F9;
          border-radius: 8px;
          padding: 3px; gap: 2px;
        }
        .plba-tab {
          padding: 6px 16px; border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748B;
          cursor: pointer; border: none; background: transparent;
          transition: background 0.15s, color 0.15s;
          white-space: nowrap;
        }
        .plba-tab:hover { background: #ffffff; color: #0F172A; }
        .plba-tab.plba-active { background: #ffffff; color: #2563EB; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

        .plba-search {
          display: flex; align-items: center; gap: 8px;
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          padding: 9px 14px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          width: 280px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .plba-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .plba-search input {
          border: none; outline: none; background: transparent;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px; color: #0F172A; width: 100%;
        }
        .plba-search input::placeholder { color: #94A3B8; }

        .plba-table { width: 100%; border-collapse: collapse; }
        .plba-table thead tr { background: #F8FAFC; }
        .plba-table th {
          padding: 11px 16px; text-align: left;
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.07em;
          border-bottom: 1px solid #E2E8F0;
        }
        .plba-table td {
          padding: 14px 16px; font-size: 14px; color: #1E293B;
          border-bottom: 1px solid #F1F5F9; vertical-align: middle;
        }
        .plba-table tbody tr { transition: background 0.12s; }
        .plba-table tbody tr:hover { background: #F8FAFC; }
        .plba-table tbody tr:last-child td { border-bottom: none; }

        .plba-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; border-radius: 20px;
          font-size: 12px; font-weight: 600;
        }

        .plba-page-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 16px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .plba-page-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .plba-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .plba-dl-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #1E293B;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .plba-dl-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }

        .plba-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; background: #EFF6FF; border-radius: 20px;
          font-size: 12px; font-weight: 700; color: #2563EB;
        }

        .plba-link { color: #2563EB; font-weight: 600; font-size: 13px; cursor: pointer; text-decoration: none; }
        .plba-link:hover { text-decoration: underline; }

        .plba-name-cell { font-weight: 600; color: #0F172A; }
        .plba-amount-cell { font-family: 'DM Mono', monospace; font-size: 13px; }
      `}</style>

      <div className="plba-root w-full overflow-visible">
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* ── Page header ── */}
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>
              Post Pre-LBA Actions
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Get to know time remaining for your LBA
            </p>
          </div>

          {/* ── Metric cards ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
            {metricCards.map((card) => (
              <div key={card.label} className="plba-card plba-metric-card" style={{ padding: 24 }}>
                <div className="accent-bar" style={{ background: card.accentBar }} />
                <div style={{ marginTop: 8 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: card.iconBg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginBottom: 18, border: "1px solid #E2E8F0",
                  }}>
                    {card.icon}
                  </div>
                  <div style={{
                    fontSize: 12, fontWeight: 600, color: "#94A3B8",
                    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
                  }}>
                    {card.label}
                  </div>
                  <div style={{
                    fontFamily: "'DM Mono', monospace", fontSize: 28,
                    fontWeight: 500, color: "#0F172A", lineHeight: 1.1,
                  }}>
                    {card.format === "currency"
                      ? `£${(card.value || 0).toFixed(2)}`
                      : card.value ?? 0}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Table card ── */}
          <div className="plba-card" style={{ overflow: "hidden" }}>
            {/* Table header */}
            <div style={{
              padding: "18px 24px", borderBottom: "1px solid #E2E8F0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Clients</span>
                <span className="plba-count-pill">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563EB", display: "inline-block" }} />
                  {searchedDebtors.length === 1 ? "1 entry" : `${searchedDebtors.length || "No"} entries`}
                </span>
              </div>
              <button className="plba-dl-btn" onClick={openDownloadModalTables}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Download
              </button>
            </div>

            {/* Filter row */}
            <div style={{
              padding: "12px 24px", borderBottom: "1px solid #F1F5F9",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#FAFBFD",
            }}>
              <div className="plba-tab-row">
                {["all", "High Risk", "Medium Risk", "Low Risk"].map((risk) => (
                  <button
                    key={risk}
                    className={`plba-tab${activeTab === risk ? " plba-active" : ""}`}
                    onClick={() => setActiveTab(risk)}
                  >
                    {risk === "all" ? "View all" : risk}
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="plba-search">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2"
                      stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search clients…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button className="plba-dl-btn" onClick={openDateFilter}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M3 7h18M6 12h12M10 17h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Filter
                </button>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table className="plba-table">
                <thead>
                  <tr>
                    {["Debtor", "Amount", "Date issued", "Risk", "Action"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedDebtors.length > 0 ? (
                    paginatedDebtors.map((lists) => {
                      const riskStyle = riskStyleMap[lists.risk] || { background: "#F1F5F9", color: "#475569" };
                      return (
                        <tr key={lists.id}>
                          <td className="plba-name-cell">{lists.debtor}</td>
                          <td className="plba-amount-cell">{lists.amount}</td>
                          <td style={{ color: "#64748B", fontSize: 13 }}>{lists.date}</td>
                          <td>
                            <span
                              className="plba-badge"
                              style={{ background: riskStyle.background, color: riskStyle.color }}
                            >
                              ● {lists.risk}
                            </span>
                          </td>
                          <td>
                            <a
                              className="plba-link"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                onSeeDetailsTextClick(
                                  lists.id, lists.debtor, lists.amount,
                                  lists.date, lists.risk, lists.address, lists.email
                                );
                              }}
                            >
                              {lists.action}
                            </a>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>
                        No entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 24px", borderTop: "1px solid #E2E8F0", background: "#FAFBFD",
            }}>
              <button
                className="plba-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                ← Previous
              </button>
              <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                className="plba-page-btn"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              >
                Next →
              </button>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* ── Modals ── */}
      {isNavigationDropdownOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeNavigationDropdown}>
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      {isDownloadModalTablesOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDownloadModalTables}>
          <DownloadModalTables onClose={closeDownloadModalTables} tableData={paginatedDebtors} tag="post-prelba" />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isDateFilterOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDateFilter}>
          <DateFilter onClose={closeDateFilter} onDateTimeSelect={handleDateTimeSelect} />
        </PortalPopup>
      )}
      {isExportModalImageOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeExportModalImage}>
          <ExportModalImage onClose={closeExportModalImage} />
        </PortalPopup>
      )}
    </>
  );
};

export default PostLBAActionsPage;